interface UserProfileData {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  phoneNumber?: string;
  workspaceId: string;
  role: string;
  preferences: {
    notifications?: {
      email?: boolean;
      sms?: boolean;
      slack?: boolean;
      inApp?: boolean;
    };
    [key: string]: any;
  };
  metadata: Record<string, any>;
  createdAt?: string;
  updatedAt?: string;
}

export class EngineeringProfileService {
  private static instance: EngineeringProfileService;

  static getInstance(): EngineeringProfileService {
    if (!this.instance) {
      this.instance = new EngineeringProfileService();
    }
    return this.instance;
  }

  async saveProfile(userData: UserProfileData, mode: 'demo' | 'live'): Promise<UserProfileData> {
    if (mode === 'demo') {
      const profileData = {
        ...userData,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      localStorage.setItem('engineering-demo-profile', JSON.stringify(profileData));
      return profileData;
    } else {
      try {
        const token = localStorage.getItem('engineering-auth-token');
        const response = await fetch('/api/users/me', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(userData)
        });
        
        if (!response.ok) {
          throw new Error('Failed to save profile');
        }
        
        return await response.json();
      } catch (error) {
        console.error('Failed to save profile:', error);
        throw error;
      }
    }
  }

  async updateProfile(userId: string, updates: Partial<UserProfileData>, mode: 'demo' | 'live'): Promise<UserProfileData> {
    if (mode === 'demo') {
      const existing = this.getProfile(userId, mode);
      if (!existing) {
        throw new Error('Profile not found');
      }
      const updated = { 
        ...existing, 
        ...updates, 
        updatedAt: new Date().toISOString() 
      };
      localStorage.setItem('engineering-demo-profile', JSON.stringify(updated));
      return updated;
    } else {
      try {
        const token = localStorage.getItem('engineering-auth-token');
        const response = await fetch('/api/users/me', {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(updates)
        });
        
        if (!response.ok) {
          throw new Error('Failed to update profile');
        }
        
        return await response.json();
      } catch (error) {
        console.error('Failed to update profile:', error);
        throw error;
      }
    }
  }

  getProfile(userId: string, mode: 'demo' | 'live'): UserProfileData | null {
    if (mode === 'demo') {
      const stored = localStorage.getItem('engineering-demo-profile');
      return stored ? JSON.parse(stored) : null;
    } else {
      return null;
    }
  }

  async fetchProfile(mode: 'demo' | 'live'): Promise<UserProfileData | null> {
    if (mode === 'demo') {
      return this.getProfile('demo-user', mode);
    } else {
      try {
        const token = localStorage.getItem('engineering-auth-token');
        if (!token) return null;
        
        const response = await fetch('/api/users/me', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch profile');
        }
        
        return await response.json();
      } catch (error) {
        console.error('Failed to fetch profile:', error);
        return null;
      }
    }
  }

  clearProfile(mode: 'demo' | 'live'): void {
    if (mode === 'demo') {
      localStorage.removeItem('engineering-demo-profile');
    } else {
      localStorage.removeItem('engineering-auth-token');
    }
  }
}
